import { ScheduleBlock, Suggestion } from "@aika/types";
import axios from "axios";
import { IconButton } from "components/IconButton";
import { TagInput } from "components/TagInput";
import { format, getUnixTime } from "date-fns";
import { useBrushes } from "hooks/BrushesProvider";
import { compose } from "lodash/fp";
import { desaturate, lighten } from "polished";
import React, { useEffect, useRef, useState } from "react";
import { FaPalette, FaTrash } from "react-icons/fa";
import { animated, config, useSpring } from "react-spring";
import styled, { css, useTheme } from "styled-components";
import { Box } from "../../components/Box";
import Button from "../../components/Button";
import { Flex } from "../../components/Flex";
import { FormInput } from "../../components/FormInput";
import { Panel, PanelHeader } from "../../components/Panel";
import { Text } from "../../components/Text";

const dim = compose(lighten(0.1), desaturate(0.1));

const Container = styled(animated.div)<{
  left: number;
  top: number;
  origin: string;
}>`
  position: absolute;
  left: ${(props) => props.left}px;
  top: ${(props) => props.top}px;
  z-index: 1000;
  width: 250px;
  transform-origin: ${(props) => props.origin};
`;

const ColorBlock = styled.span<{ colorIndex: number }>`
  background-color: ${(props) => props.theme.blockColors[props.colorIndex]};
  width: 24px;
  height: 24px;
  border: 2px solid ${(props) => props.theme.blockColors[props.colorIndex]};
  margin-right: 6px;
`;

const SuggestionButton = styled.button`
  display: flex;
  width: 100%;
  padding: 6px;
  align-items: center;
  border: 0;
  background: 0;
  cursor: pointer;

  &:hover {
    background: ${(props) => props.theme.colors.grey0};
  }
`;

const ColorButton = styled.button<{ active: boolean; colorIndex: number }>`
  background-color: ${(props) => props.theme.blockColors[props.colorIndex]};
  width: 26px;
  height: 26px;
  display: block;

  border: 2px solid ${(props) => props.theme.blockColors[props.colorIndex]};
  ${(props) => {
    if (!props.active) {
      return "";
    }

    const c1 = props.theme.blockColors[props.colorIndex];
    const c2 = dim(c1);

    return css`
      background-image: linear-gradient(
        45deg,
        ${c1} 25%,
        ${c2} 25%,
        ${c2} 50%,
        ${c1} 50%,
        ${c1} 75%,
        ${c2} 75%,
        ${c2} 100%
      );
      background-size: 10px 10px;
    `;
  }};
  &:focus,
  &:active {
    outline: 0;
  }
`;

type ItemInfoDelta = {
  description: string;
  color: number;
  tags: string[];
};

type ItemInfoProps = {
  item: ScheduleBlock;
  onChange: (delta: ItemInfoDelta) => void;
  onDelete: () => void;
  onCancel: () => void;
  left: number;
  top: number;
  fromLeft: boolean;
};

const ItemInfo = ({
  item,
  onChange,
  onDelete,
  onCancel,
  left,
  top,
  fromLeft,
}: ItemInfoProps) => {
  const [description, setDescription] = useState(item.description);
  const [tags, setTags] = useState(item.tags);
  const [color, setColor] = useState(item.color);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const desRef = useRef<HTMLInputElement>(null!);
  const idRef = useRef<string>(item.id);
  const theme = useTheme();
  const { saveSwatch, swatchExists } = useBrushes();

  const { start, length, date } = item;

  const asSwatch = () => ({
    tags,
    description,
    color,
    createdOn: getUnixTime(new Date()),
  });

  const createAsSwatch = () => {
    saveSwatch(asSwatch());
  };

  useEffect(() => {
    if (idRef.current !== item.id) {
      idRef.current = item.id;
      setDescription(item.description);
      setColor(item.color);
      setTags(item.tags);
      desRef.current?.focus();
    }
  }, [item]);

  useEffect(() => {
    axios
      .get(
        `/api/suggestions?date=${format(
          date,
          "yyyy-MM-dd"
        )}&start=${start}&length=${length}`
      )
      .then(({ data }) => {
        setSuggestions(data);
      });
  }, [start, length, date]);

  const containerProps = useSpring({
    transform: "scale3d(1,1,1)",
    opacity: 1,
    from: {
      transform: "scale3d(0.5,0.5,0.5)",
      opacity: 0,
    },
    config: config.stiff,
  });

  const commit = (ev: React.FormEvent) => {
    ev.preventDefault();
    onChange({
      description,
      color,
      tags: tags,
    });
  };

  const suggest = (x: Suggestion) => {
    onChange({
      description: x.description,
      color: x.color,
      tags: x.tags,
    });
  };

  const handleKeyDown = (ev: React.KeyboardEvent) => {
    if (ev.keyCode === 27) {
      onCancel();
    }
  };

  const exists = swatchExists(asSwatch());
  const validSwatch = description.trim() !== "" || tags.length > 0;

  return (
    <Container
      tabIndex={0}
      left={left}
      top={top}
      origin={fromLeft ? "top right" : "top left"}
      style={containerProps}
      onKeyDown={handleKeyDown}
    >
      <Panel boxShadow={2} p={0}>
        <PanelHeader>
          <Flex justifyContent="space-between" alignItems="center">
            <Text fontSize={4} fontWeight={600}>
              Edit Item
            </Text>
            <IconButton
              onClick={createAsSwatch}
              icon={FaPalette}
              title={
                "Create As New Swatch" +
                (exists
                  ? " (Swatch Exists)"
                  : !validSwatch
                  ? " (Not Valid Swatch)"
                  : "")
              }
              disabled={exists || !validSwatch}
            />
          </Flex>
        </PanelHeader>
        <Box p={3}>
          <form onSubmit={commit}>
            <FormInput
              name="description"
              label="Description"
              value={description}
              onChange={(ev) => setDescription(ev.currentTarget.value)}
              autoComplete="off"
              autoFocus={true}
              ref={desRef}
              onClear={() => setDescription("")}
            />
            <TagInput value={tags} onChange={(val) => setTags(val)} />
            <Flex mb={3} flexWrap="wrap" justifyContent="center">
              {theme.blockColors.map((_x, i) => (
                <ColorButton
                  key={i}
                  colorIndex={i}
                  type="button"
                  onClick={() => setColor(i)}
                  active={color === i}
                />
              ))}
            </Flex>
            <Flex justifyContent="space-between" alignItems="flex-end">
              <Button type="submit">Save</Button>
              <IconButton onClick={onDelete} icon={FaTrash} />
            </Flex>
          </form>
          {suggestions.length > 0 && (
            <>
              <Box mt={3} mb={2} style={{ borderTop: "1px solid #aaa" }} />
              <Text fontStyle="italic" fontSize={2} mb={1}>
                Suggestions
              </Text>
              {suggestions.map((x) => (
                <SuggestionButton onClick={() => suggest(x)}>
                  <ColorBlock colorIndex={x.color} />
                  <Text>{x.description}</Text>
                  {x.tags.length > 0 && (
                    <Text ml={1} fontSize={2}>
                      (+{x.tags.length} tag{x.tags.length === 1 ? "" : "s"})
                    </Text>
                  )}
                </SuggestionButton>
              ))}
            </>
          )}
        </Box>
      </Panel>
    </Container>
  );
};

export default ItemInfo;
